import { SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { LeaveEmployeeDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import TextArea from 'modules/core/components/Inputs/TextArea'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { leaveConfirmationFields } from 'modules/timeoff/components/EditLeaveModal/useLeaveData'
import LeaveTypeFormInput from 'modules/timeoff/components/LeaveTypeFormInput'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import DayTypeForm from './DayTypeForm'
import Documents from './Documents'
import EauResponse from './electronicSickLeave/EauResponse'
import NewEauRequest from './electronicSickLeave/NewEauRequest'

interface Props {
  readOnly?: boolean
  leaveTypeReadOnly: boolean
  form: LeaveForm
  documentForm: DocumentForm
  customFieldsForms: Form[]
  dayTypeForm: LeaveDayTypeForm
  dayTypeOptions: () => SelectOption[]
  leave?: Leave
  leaveTypes: LeaveTypes
  onDocumentDropped?: () => void
  hasMedicalLeavesOpen?: (employeeId: number) => boolean
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  selectedLeaveType?: LeaveType
  canCreateEauRequestOauthToken: boolean
  canApplyForEAU: boolean
  leaveConfirmationDetails?: leaveConfirmationFields
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  leaveCanManageLeaveDocuments: boolean
  employeeCanManageLeavesDocuments: boolean
  leaveEmployee?: LeaveEmployeeDataFragment
}

const OtherLeavesForm = ({
  readOnly = false,
  leaveTypeReadOnly,
  form,
  documentForm,
  customFieldsForms,
  dayTypeForm,
  dayTypeOptions,
  leave,
  leaveTypes,
  onDocumentDropped,
  hasMedicalLeavesOpen,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  selectedLeaveType,
  canCreateEauRequestOauthToken,
  canApplyForEAU,
  leaveConfirmationDetails,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  leaveCanManageLeaveDocuments,
  employeeCanManageLeavesDocuments,
  leaveEmployee,
}: Props) => {
  const isMandatory = selectedLeaveType?.attributes.details_required
  const leaveId = leave?.id
  const leaveTypeId = form.data().leave_type_id

  const getErrorMessages = (messages: unknown) => {
    if (!messages || !Array.isArray(messages)) return null

    return messages.map((msg) => {
      if (typeof msg === 'object' && msg !== null && 'error' in msg) {
        return msg
      }
      return { error: String(msg) }
    })
  }

  const isSickLeave = (): boolean => {
    if (leave && leave.leaveType) {
      return !!leave?.leaveType?.isSick
    }

    if (!leaveTypeId) return false
    if (!leaveTypes.length) return false

    const leaveType = leaveTypes.get(leaveTypeId)
    if (!leaveType) return false

    return leaveType.isSick
  }

  return (
    <>
      <LeaveTypeFormInput
        form={form}
        leave={leave}
        availableLeaveTypes={leaveTypes.models}
        readOnly={readOnly || leaveTypeReadOnly}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        selectedLeaveType={selectedLeaveType}
        leaveEmployee={leaveEmployee}
      />

      {hasFeature('timeoff_electronic_sick_leave') &&
        isSickLeave() &&
        canApplyForEAU &&
        (!leave ? (
          <FormRow>
            <NewEauRequest
              form={form}
              canCreateEauRequestOauthToken={canCreateEauRequestOauthToken}
            />
          </FormRow>
        ) : (
          leaveConfirmationDetails &&
          Object.keys(leaveConfirmationDetails).length > 0 && (
            <FormRow>
              <EauResponse
                confirmedStartDate={
                  leaveConfirmationDetails?.confirmed_start_date
                    ? new Date(leaveConfirmationDetails.confirmed_start_date)
                    : null
                }
                confirmedEndDate={
                  leaveConfirmationDetails?.confirmed_end_date
                    ? new Date(leaveConfirmationDetails.confirmed_end_date)
                    : null
                }
                status={leaveConfirmationDetails?.status}
                leave={leave}
                updatedAt={new Date(leaveConfirmationDetails?.updated_at)}
                errorMessages={getErrorMessages(leaveConfirmationDetails?.error_messages)}
              />
            </FormRow>
          )
        ))}

      <FormRow>
        <TextArea
          maxLength={250}
          readonly={readOnly}
          label={i18n.t('time_off.description')}
          field={form.get('description')}
          mandatory={isMandatory}
        />
      </FormRow>
      <FormRow>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          readonly={readOnly}
          field={dayTypeForm.get('day_type')}
          options={dayTypeOptions()}
        />
      </FormRow>
      <DayTypeForm
        readOnly={readOnly}
        form={form}
        dayTypeForm={dayTypeForm}
        companyHolidaysMuncher={companyHolidaysMuncher}
        blockedPeriodsMuncher={blockedPeriodsMuncher}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={isReviewOrEditMode}
        leaveId={leaveId}
        leaveTypeId={leaveTypeId}
      />
      {form.acceptsAttachment() && (
        <Documents
          customFieldsForms={customFieldsForms}
          documentForm={documentForm}
          leaveType={leaveTypes.get(form.data().leave_type_id)}
          onDocumentDropped={onDocumentDropped}
          leaveDocuments={leave?.documents || []}
          canManageLeaveDocuments={leaveCanManageLeaveDocuments || employeeCanManageLeavesDocuments}
        />
      )}
    </>
  )
}

export default observer(OtherLeavesForm)
